// src/components/calculators/QuoteBuilder.js
import React, { useState } from 'react';
import { 
  ArrowLeftIcon, 
  CurrencyPoundIcon,
  DocumentDuplicateIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from "@heroicons/react/24/outline";
import { useCalculator } from './CalculatorContext';
import ResultsDisplay from './ResultsDisplay';
import VATToggle from './VATToggle';

const QuoteBuilder = ({ onBack }) => {
  const {
    quoteBuilderData,
    setQuoteBuilderData,
    quoteBuilderResults,
    validateInput,
    formatCurrency,
    formatPercentage
  } = useCalculator();

  const [copiedPrice, setCopiedPrice] = useState(false);

  const handleInputChange = (field) => (e) => {
    const value = e.target.value;
    if (validateInput(value) || value === '') {
      setQuoteBuilderData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleCopyPrice = () => {
    if (quoteBuilderResults) {
      navigator.clipboard.writeText(quoteBuilderResults.sellingPrice.toFixed(2));
      setCopiedPrice(true);
      setTimeout(() => setCopiedPrice(false), 2000);
    }
  };

  const handleClearAll = () => {
    setQuoteBuilderData({
      costOfGoods: '',
      targetProfit: '',
      vatRate: '20',
      applyVAT: false
    });
  };

  // Validation
  const hasError = () => {
    const cost = parseFloat(quoteBuilderData.costOfGoods);
    const profit = parseFloat(quoteBuilderData.targetProfit);
    return (cost === 0 && profit === 0) || cost < 0 || profit < 0;
  };

  const styles = {
    container: {
      fontFamily: 'var(--font-family-primary)'
    },
    backButton: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 'var(--spacing-xs)',
      padding: 'var(--spacing-xs) var(--spacing-m)',
      background: 'var(--Button-Primary-Enabled-BG)',
      color: 'var(--Button-Primary-Text)',
      border: 'none',
      borderRadius: 'var(--button-radius)',
      fontSize: 'var(--font-size-button)',
      fontWeight: 'var(--button-font-weight)',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      marginBottom: 'var(--spacing-l)'
    },
    // FIX: Removed the headerBanner style and updated the title style below
    title: {
      fontSize: 'var(--font-size-h4)',
      fontWeight: 'var(--font-weight-bold)',
      color: 'var(--Text-Primary)', // Changed from --Text-Contrast
      marginBottom: 'var(--spacing-xl)' // Added margin
    },
    errorAlert: {
      background: 'var(--Surface-Error)',
      border: '1px solid var(--Stroke-Error)',
      borderRadius: 'var(--radius-s)',
      padding: 'var(--spacing-m)',
      display: 'flex',
      gap: 'var(--spacing-s)',
      marginBottom: 'var(--spacing-l)'
    },
    errorIcon: {
      width: '20px',
      height: '20px',
      color: 'var(--Red-600)',
      flexShrink: 0
    },
    errorText: {
      fontSize: 'var(--font-size-body4)',
      color: 'var(--Text-Error)',
      lineHeight: 'var(--line-height-normal)'
    },
    card: {
      background: 'var(--Surface-Primary)',
      borderRadius: 'var(--radius-m)',
      boxShadow: 'var(--shadow-lg)',
      border: '1px solid var(--Stroke-Light)',
      overflow: 'hidden'
    },
    cardHeader: {
      background: 'var(--Red-500)',
      padding: 'var(--spacing-m)',
      borderBottom: '1px solid var(--Green-100)'
    },
    cardTitle: {
      fontSize: 'var(--font-size-body2)',
      fontWeight: 'var(--font-weight-semibold)',
      color: 'var(--Text-Contrast)',
      margin: 0
    },
    cardBody: {
      padding: 'var(--spacing-l)',
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--spacing-l)'
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column'
    },
    label: {
      fontSize: 'var(--font-size-body4)',
      fontWeight: 'var(--font-weight-medium)',
      color: 'var(--Text-Primary)',
      marginBottom: 'var(--spacing-xs)'
    },
    inputWrapper: {
      position: 'relative'
    },
    input: {
      width: '100%',
      padding: 'var(--spacing-xs) var(--spacing-s)',
      paddingLeft: 'calc(var(--spacing-s) * 2.5)',
      border: 'var(--input-border)',
      borderRadius: 'var(--input-radius)',
      fontSize: 'var(--font-size-body3)',
      color: 'var(--Text-Primary)',
      background: 'var(--Surface-Primary)',
      transition: 'all 0.2s ease',
      fontFamily: 'var(--font-family-primary)'
    },
    inputIcon: {
      position: 'absolute',
      left: 'var(--spacing-s)',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '20px',
      height: '20px',
      color: 'var(--Text-Tertiary)'
    },
    helperText: {
      fontSize: 'var(--font-size-body5)',
      color: 'var(--Text-Tertiary)',
      marginTop: 'var(--spacing-xxs)'
    },
    clearButton: {
      background: 'transparent',
      color: 'var(--Red-600)',
      border: 'none',
      padding: 'var(--spacing-xs)',
      fontSize: 'var(--font-size-body4)',
      fontWeight: 'var(--font-weight-medium)',
      cursor: 'pointer',
      transition: 'color 0.2s ease',
      fontFamily: 'var(--font-family-primary)'
    },
    heroCard: {
      background: 'var(--brand-gradient-4)',
      borderRadius: 'var(--radius-m)',
      color: 'var(--Text-Contrast)',
      padding: 'var(--spacing-xl)',
      textAlign: 'center',
      marginBottom: 'var(--spacing-m)'
    },
    heroLabel: {
      fontSize: 'var(--font-size-body4)',
      opacity: 0.9,
      marginBottom: 'var(--spacing-xs)',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: 'var(--Grey-900)',
      fontWeight: 'var(--font-weight-semibold)'
    },
    heroPrice: {
      fontSize: 'var(--font-size-h3)',
      fontWeight: 'var(--font-weight-bold)',
      marginBottom: 'var(--spacing-m)'
    },
    copyButton: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 'var(--spacing-xs)',
      padding: 'var(--spacing-xs) var(--spacing-m)',
      background: 'white',
      color: 'var(--Grey-900)',
      border: 'none',
      borderRadius: 'var(--button-radius)',
      fontSize: 'var(--font-size-body4)',
      fontWeight: 'var(--button-font-weight)',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    },
    emptyState: {
      minHeight: '400px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: '2px dashed var(--Grey-300)',
      borderRadius: 'var(--radius-m)',
      background: 'var(--Surface-Primary)'
    },
    emptyIcon: {
      width: '48px',
      height: '48px',
      color: 'var(--Grey-400)',
      marginBottom: 'var(--spacing-m)'
    },
    emptyTitle: {
      fontSize: 'var(--font-size-body2)',
      fontWeight: 'var(--font-weight-medium)',
      color: 'var(--Text-Secondary)',
      marginBottom: 'var(--spacing-xs)'
    },
    emptyText: {
      fontSize: 'var(--font-size-body4)',
      color: 'var(--Text-Tertiary)'
    }
  };

  return (
    <div style={styles.container}>
      {/* Back Button */}
      <button
        style={styles.backButton}
        onClick={onBack}
        onMouseEnter={e => {
          e.currentTarget.style.background = 'var(--Button-Primary-Hovered-BG)';
          e.currentTarget.style.gap = 'var(--spacing-s)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background = 'var(--Button-Primary-Enabled-BG)';
          e.currentTarget.style.gap = 'var(--spacing-xs)';
        }}
      >
        <ArrowLeftIcon style={{ width: '16px', height: '16px' }} />
        Back
      </button>

      {/* FIX: Header is now a standalone heading */}
      <h3 style={styles.title}>Quote Builder</h3>

      {/* Error Alert */}
      {hasError() && (
        <div style={styles.errorAlert}>
          <ExclamationTriangleIcon style={styles.errorIcon} />
          <p style={styles.errorText}>
            Please enter valid amounts. Cost and profit must be positive numbers, 
            and at least one must be greater than zero.
          </p>
        </div>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: 'var(--spacing-xl)'
      }}>
        {/* Input Section */}
        <div>
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h6 style={styles.cardTitle}>Define Your Costs & Profit</h6>
            </div>
            <div style={styles.cardBody}>
              {/* Cost Input */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Cost of Goods/Materials
                </label>
                <div style={styles.inputWrapper}>
                  <input
                    type="text"
                    value={quoteBuilderData.costOfGoods}
                    onChange={handleInputChange('costOfGoods')}
                    placeholder="0.00"
                    style={styles.input}
                    onFocus={e => {
                      e.currentTarget.style.borderColor = 'var(--Input-Border-Focused)';
                      e.currentTarget.style.boxShadow = 'var(--Input-Focus-Ring)';
                    }}
                    onBlur={e => {
                      e.currentTarget.style.borderColor = 'var(--Input-Border-Default)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  />
                  <CurrencyPoundIcon style={styles.inputIcon} />
                </div>
                <p style={styles.helperText}>
                  Your actual cost for materials, labor, or product
                </p>
              </div>

              {/* Target Profit Input */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Target Profit (Before VAT)
                </label>
                <div style={styles.inputWrapper}>
                  <input
                    type="text"
                    value={quoteBuilderData.targetProfit}
                    onChange={handleInputChange('targetProfit')}
                    placeholder="0.00"
                    style={styles.input}
                    onFocus={e => {
                      e.currentTarget.style.borderColor = 'var(--Input-Border-Focused)';
                      e.currentTarget.style.boxShadow = 'var(--Input-Focus-Ring)';
                    }}
                    onBlur={e => {
                      e.currentTarget.style.borderColor = 'var(--Input-Border-Default)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  />
                  <CurrencyPoundIcon style={styles.inputIcon} />
                </div>
                <p style={styles.helperText}>
                  Desired profit after costs and Boutee fees
                </p>
              </div>

              {/* VAT Toggle */}
              <VATToggle
                applyVAT={quoteBuilderData.applyVAT}
                vatRate={quoteBuilderData.vatRate}
                onChange={(field, value) => 
                  setQuoteBuilderData(prev => ({ ...prev, [field]: value }))
                }
              />

              {/* Clear Button */}
              <button
                style={styles.clearButton}
                onClick={handleClearAll}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--Red-700)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--Red-600)'}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>

        {/* Results Section */}
        <div>
          {quoteBuilderResults && !hasError() ? (
            <>
              {/* Quote Price Hero */}
              <div style={styles.heroCard}>
                <p style={styles.heroLabel}>
                  QUOTE THIS PRICE
                </p>
                <h2 style={styles.heroPrice}>
                  {formatCurrency(quoteBuilderResults.sellingPrice)}
                </h2>
                <button
                  style={styles.copyButton}
                  onClick={handleCopyPrice}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--Grey-50)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'white'}
                >
                  {copiedPrice ? (
                    <>
                      <CheckCircleIcon style={{ width: '16px', height: '16px' }} />
                      Copied!
                    </>
                  ) : (
                    <>
                      <DocumentDuplicateIcon style={{ width: '16px', height: '16px' }} />
                      Copy Price
                    </>
                  )}
                </button>
              </div>

              {/* Detailed Breakdown */}
              <ResultsDisplay 
                results={quoteBuilderResults}
                mode="quoteBuilder"
              />
            </>
          ) : (
            <div style={styles.emptyState}>
              <div style={{ textAlign: 'center', padding: 'var(--spacing-l)' }}>
                <div style={{
                  padding: 'var(--spacing-m)',
                  background: 'var(--Grey-100)',
                  borderRadius: 'var(--radius-pill)',
                  display: 'inline-block',
                  marginBottom: 'var(--spacing-m)'
                }}>
                  <DocumentDuplicateIcon style={styles.emptyIcon} />
                </div>
                <h6 style={styles.emptyTitle}>
                  No quote generated yet
                </h6>
                <p style={styles.emptyText}>
                  Enter your costs and target profit to build a quote
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuoteBuilder;